import { useEffect, useRef, type RefObject } from 'react';

/**
 * Pause looping GSAP animations while their container is offscreen so nav/CTA
 * interactions aren't competing with DrawSVG work on the main thread.
 *
 * Returns `isVisibleRef` so builders can create their timelines `paused` and
 * only `play()` them when the section is actually in view — otherwise a
 * timeline built after the observer's initial callback would run invisibly
 * until the user first scrolls near it.
 */
export function usePauseTimelineWhenOffscreen(
  containerRef: RefObject<HTMLElement | null>,
  ...animationRefs: RefObject<gsap.core.Animation | null>[]
) {
  const isVisibleRef = useRef(false);
  // Refs are stable objects, so holding them in a ref keeps the effect's
  // dependency list fixed regardless of how many are passed.
  const refsRef = useRef(animationRefs);
  refsRef.current = animationRefs;

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        const visible = !!entry?.isIntersecting;
        isVisibleRef.current = visible;
        for (const ref of refsRef.current) {
          const animation = ref.current;
          if (!animation) continue;
          if (visible) animation.play();
          else animation.pause();
        }
      },
      // Tight margin so DrawSVG work stops once the diagram leaves the
      // viewport — keeps the main thread freer during scroll-up past the
      // rails / painpoint bands (where iOS reported jumpy scrolling).
      { rootMargin: '0px 0px', threshold: 0.1 },
    );
    io.observe(root);
    return () => io.disconnect();
  }, [containerRef]);

  return isVisibleRef;
}
